import React, { useEffect, useReducer } from 'react';
import {
    HorizontalNoteContainer,
    NoteRangeType
} from '../../model/containers';
import {
    Note,
    NOTES_DOMAIN,
    TimeSignature
} from '../../model/core';
import { HarmonicPredicates } from '../../utils/harmonyHighlight';
import { ScrollController } from '../../utils/ScrollController';
import { NoteSelectionGroup } from '../../utils/NoteSelectionGroup';
import {
    useNoteSelectionGroups,
    useSetNoteSelectionGroups
} from '../../stores/InspectorPanelStore';
import {
    useCopyBuffer,
    useSetCopyBuffer
} from '../../stores/CopyBufferStore';
import {
    useCopyDropOverlayGeometry,
    useSetCopyBufferOverlayIsActive
} from '../../stores/CopyDropOverlayStore';
import { CopyBuffer } from '../../utils/copyBuffer';
import { PianoRollBackground } from '../widgets/rolls/PianoRollBackground';
import {
    CopyDropOverlay,
    SelectedRegionOverlay
} from '../overlays/CopyPaste';
import { useTimelineSelection } from '../../stores/TimelineSelectionStore';


export function PianoRollInputLayer({
    trackId,
    hnc,
    timeSignature,
    noteRange,
    harmonicPredicates,
    addNoteToContainer,
    scrollController,
    setHNC,
}: {
    trackId: number,
    hnc: HorizontalNoteContainer,
    timeSignature: TimeSignature,
    noteRange: NoteRangeType,
    harmonicPredicates: HarmonicPredicates,
    addNoteToContainer: (start: number, note: Note) => void,
    scrollController?: ScrollController,
    setHNC: (hnc: HorizontalNoteContainer) => void,
}) {

    const workspaceTimelineSelection = useTimelineSelection();
    const copyDropOverlayGeometry = useCopyDropOverlayGeometry();
    const setCopyDropOverlayIsActive = useSetCopyBufferOverlayIsActive();
    const noteSelectiongGroups = useNoteSelectionGroups();
    const setWorkspaceSelectionGroups = useSetNoteSelectionGroups();
    const copyBuffer = useCopyBuffer();
    const setCopyBuffer = useSetCopyBuffer();

    const forceUpdate = useReducer(x => x + 1, 0)[1];
    const [listenerId, setListenerId] = React.useState<number>();

    useEffect(() => {
        if (!scrollController) return;
        scrollController.listen((n) => {
            forceUpdate();
        });
    }, [scrollController]);

    useEffect(() => {
        if (!listenerId) return;
        scrollController?.drop(listenerId);
    }, []);

    useEffect(() => {
        // updates the selection group to match the timeline selection
        if (!workspaceTimelineSelection) {
            noteSelectiongGroups.delete(trackId);
            setWorkspaceSelectionGroups(new Map(noteSelectiongGroups));
            return;
        }

        const newSelection = new NoteSelectionGroup(hnc, {
            start: workspaceTimelineSelection.start,
            left: workspaceTimelineSelection.start,
            top: NOTES_DOMAIN.max,
            width: workspaceTimelineSelection.end - workspaceTimelineSelection.start,
            height: NOTES_DOMAIN.max
        });
       hnc.getGrid(true, { start: workspaceTimelineSelection.start, length: workspaceTimelineSelection.end - workspaceTimelineSelection.start })
            .flat()
            .forEach(n => newSelection.addNote(n.step, n.payload, n.duration, n.velocity));
        noteSelectiongGroups.set(trackId, newSelection);
        setWorkspaceSelectionGroups(new Map(noteSelectiongGroups));
    }, [workspaceTimelineSelection]);

    // COPY/PASTE
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent, sg?: NoteSelectionGroup) => {
            if (e.ctrlKey && e.code === 'KeyC') {
                if (!noteSelectiongGroups) return;
                const buf = new CopyBuffer(noteSelectiongGroups.get(trackId)!);
                setCopyBuffer(buf);
                return;
            }

            if (!copyBuffer) return;

            if (e.ctrlKey && e.code === 'KeyV') {
                copyBuffer.transition(() => {
                    // actual do the copy
                    if (copyDropOverlayGeometry) {
                        copyBuffer.getSelection().getNotes().forEach((n) => {
                            const shiftX = 0 | (copyBuffer.getSelection().getGeometry().start - copyDropOverlayGeometry.start);
                            const shiftY = copyBuffer.getSelection().getGeometry().top - copyDropOverlayGeometry.topNote;
                            const newStart = n.step - shiftX;
                            const newNote = Note.fromIndex(n.payload.index - shiftY);

                            hnc.put(newStart, {
                                step: newStart,
                                payload: newNote,
                                duration: n.duration,
                                velocity: n.velocity
                            });
                        });

                        setHNC(hnc);
                    }
                    return 'COPY_ON_BUFFER';
                });
            }

            if (e.ctrlKey) {
                copyBuffer.transition(() => {
                    // allow styling of drop regions elsewhere
                    setCopyDropOverlayIsActive(true);
                    return 'DROP_PREPARE';
                });
                setCopyBuffer(copyBuffer);
            }

            if (e.code === 'Backspace' || e.code === 'Delete') {
                // delete selected notes from the HNC
                if (noteSelectiongGroups && noteSelectiongGroups.has(trackId)) {
                    noteSelectiongGroups.get(trackId)!.getNotes().forEach((n) => {
                        hnc.remove(n.step, {
                            step: n.step,
                            payload: n.payload,
                            duration: n.duration,
                            velocity: n.velocity
                        });
                    });

                    setHNC(hnc);

                    // reset the copy buffer state
                    setCopyDropOverlayIsActive(false);
                    copyBuffer.transition(() => 'COPY_ON_BUFFER');
                    setCopyBuffer(copyBuffer);
                } else {
                    console.warn('No selection group to delete');
                }
            }
        };

        const releaseCtrlListener = (e: KeyboardEvent) => {
            if (e.code === 'ControlLeft' || e.code === 'ControlRight') {
                if (copyBuffer && copyBuffer.getState() === 'DROP_PREPARE') {
                    copyBuffer.transition(() => {
                        // reset the copy buffer state
                        setCopyDropOverlayIsActive(false);
                        return 'COPY_ON_BUFFER';
                    });
                    setCopyBuffer(copyBuffer);
                }
            }
        };

        // FIXME: what are we doing this bound handle key down?
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', releaseCtrlListener);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', releaseCtrlListener);
        };
    }, [noteSelectiongGroups, copyBuffer, copyDropOverlayGeometry]);

    return (<div className="piano-roll-input-layer">
        <PianoRollBackground
            trackId={trackId}
            hnc={hnc}
            harmonicPredicates={harmonicPredicates}
            timeSignature={timeSignature}
            noteRange={noteRange}
            addNoteToContainer={addNoteToContainer}
        />
        <SelectedRegionOverlay
            geometry={noteSelectiongGroups.get(trackId)?.getGeometry()}
            noteRange={noteRange}
        />
        <CopyDropOverlay noteRange={noteRange} />
    </div>);
}
